import { Error } from '@mui/icons-material';
import { InputAdornment, TextField, TextFieldProps } from '@mui/material';
import React from 'react';
import {
  Control,
  Controller,
  ControllerProps,
  FieldValues,
} from 'react-hook-form';
import { getError } from '../utils';

interface ExtendedProps<T extends FieldValues> {
  name: ControllerProps<T>['name'];
  // Control prop from the react-hook-form's 'useForm'
  control: Control<T>;
  defaultValue?: ControllerProps<T>['defaultValue'];
  showIconOnFormError?: boolean;
  transformOnChangeValue?: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => string;
}

export type HookFormTextFieldProps<T extends FieldValues> = ExtendedProps<T> &
  TextFieldProps;

const getValue = (
  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
) => e.target.value;

const HookFormTextField = <T extends FieldValues>(
  props: HookFormTextFieldProps<T>,
) => {
  const {
    name,
    control,
    defaultValue,
    showIconOnFormError = true,
    transformOnChangeValue = getValue,
    ...textFieldProps
  } = props;

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field, formState: { errors } }) => {
        const parsedError = getError(errors, name);
        const hasFormError = parsedError && !!parsedError.message;
        const updatedInputProps: (typeof textFieldProps)['InputProps'] = {
          ...textFieldProps.InputProps,
        };
        if (
          showIconOnFormError &&
          hasFormError &&
          !updatedInputProps.endAdornment
        ) {
          updatedInputProps.endAdornment = (
            <InputAdornment position="end">
              <Error color="error" />
            </InputAdornment>
          );
        }
        return (
          <TextField
            {...textFieldProps}
            {...field}
            disabled={textFieldProps.disabled || field.disabled}
            InputProps={updatedInputProps}
            onChange={(e) => {
              field.onChange(transformOnChangeValue(e));
              textFieldProps.onChange?.(e);
            }}
            helperText={
              parsedError ? `${parsedError.message}` : textFieldProps.helperText
            }
            error={hasFormError || textFieldProps.error}
          />
        );
      }}
    />
  );
};

export default HookFormTextField;
